'use client';

/**
 * DynamicCriteriaGeneratorV2
 *
 * Enhanced version of the rich text editor for building AI-driven search criteria.
 * Adds slash command (/) search for quickly inserting criteria by searching values.
 *
 * Features:
 * - Type "/" to open inline search popover
 * - Search across providers, codes, payers, etc. by value
 * - Tab to quickly select first result
 * - Menu stays open for rapid multi-selection
 * - Ctrl+Space still available for type-based insertion
 *
 * @see ./ADDING_NEW_COMPONENTS.md for documentation on adding new component types
 */

import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { Typography } from 'src/components';
import { LOCATION } from 'src/hooks/filters/constants';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { ComponentMenu, SlashCommandMenu } from './components';
import {
  useSlashCommandMenu,
  useComponentPortals,
  useTemplateHydration,
} from './hooks';
import { moveCursorToEnd } from './utils/editorDomUtils';
import {
  isPlaceholderElement,
  getPlaceholderType,
} from './utils/placeholderUtils';
import {
  transformCriteriaInputsToComponents,
  ComponentData,
} from './utils/criteriaTransformer';
import { DEFAULT_ALLOWED_TYPES } from './constants';

interface DynamicCriteriaGeneratorV2Props {
  location?: string;
  allowedTypes?: CriteriaComponentType[];
}

interface Position {
  x: number;
  y: number;
}

const DynamicCriteriaGeneratorV2: React.FC<DynamicCriteriaGeneratorV2Props> = ({
  location = LOCATION.AI_LIST,
  allowedTypes,
}) => {
  const {
    updateCriteriaTemplate,
    updateComponentInput,
    removeComponentInput,
    dynamicCriteria,
  } = useAiGenerator(location);

  // Use provided allowedTypes or fall back to default
  const effectiveAllowedTypes =
    allowedTypes ||
    (DEFAULT_ALLOWED_TYPES as unknown as CriteriaComponentType[]);

  // Component menu state (Ctrl+Space)
  const [showComponentMenu, setShowComponentMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<Position>({ x: 0, y: 0 });

  const editorRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef<string>('');

  // Track placeholders that have been clicked but not yet confirmed with data
  // Maps componentId -> { placeholder: HTMLElement, confirmed: boolean }
  const pendingPlaceholdersRef = useRef<
    Map<string, { placeholder: HTMLElement; confirmed: boolean }>
  >(new Map());

  // Transform criteria_inputs to local ComponentData format using centralized transformer
  const components = useMemo(
    () => transformCriteriaInputsToComponents(dynamicCriteria?.criteria_inputs),
    [dynamicCriteria?.criteria_inputs]
  );

  const generateComponentId = () =>
    `comp_${Math.random().toString(36).substring(2, 11)}`;

  const parseContent = useCallback(
    (htmlContent: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const elements = Array.from(doc.body.childNodes);

      let template = '';
      const componentsMap = new Map<string, ComponentData>();

      elements.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          template += (node.textContent || '').replace(/\u00A0/g, ' ');
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          if (element.classList.contains('component-pill')) {
            const componentId = element.getAttribute('data-component-id');
            const componentType = element.getAttribute('data-component-type');

            if (componentId) {
              template += `{{ ${componentId} }}`;
              const existingComponent = components.get(componentId);
              if (existingComponent) {
                componentsMap.set(componentId, existingComponent);
              } else if (componentType) {
                const minimalComponent: ComponentData = {
                  id: componentId,
                  type: componentType as CriteriaComponentType,
                  codes: componentType === 'codes' ? [] : undefined,
                  providers: componentType === 'providers' ? [] : undefined,
                  specialties: componentType === 'specialties' ? [] : undefined,
                  payerTypes: componentType === 'payer_type' ? [] : undefined,
                  payerNames: componentType === 'payer_name' ? [] : undefined,
                  encounters:
                    componentType === 'provider_cohort' ? [] : undefined,
                  codeSets: componentType === 'code_set' ? [] : undefined,
                  crmStatus: componentType === 'crm_status' ? [] : undefined,
                  filterSets: componentType === 'filter_set' ? [] : undefined,
                  openPaymentsCompany:
                    componentType === 'open_payments_company' ? [] : undefined,
                  connector: 'ANY',
                  text: '',
                };
                componentsMap.set(componentId, minimalComponent);
              }
            }
          } else if (element.classList.contains('template-placeholder')) {
            // Preserve placeholder text as-is (e.g., [CODE_A])
            const placeholderText = element.getAttribute(
              'data-placeholder-text'
            );
            template += placeholderText || element.textContent || '';
          } else {
            template += (element.textContent || '').replace(/\u00A0/g, ' ');
          }
        }
      });

      return { template, components: componentsMap };
    },
    [components]
  );

  const updateCriteria = useCallback(() => {
    if (!editorRef.current) return;

    const { template } = parseContent(editorRef.current.innerHTML);

    // Only dispatch if the template actually changed to avoid unnecessary
    // Redux state updates and cascading re-renders
    if (template !== dynamicCriteria?.criteria_template) {
      updateCriteriaTemplate(template);
    }
    const domComponentIds = new Set<string>();
    const componentPills = editorRef.current.querySelectorAll(
      '[data-component-id]'
    );
    componentPills.forEach((pill) => {
      const componentId = pill.getAttribute('data-component-id');
      if (componentId) {
        domComponentIds.add(componentId);
      }
    });
    if (dynamicCriteria?.criteria_inputs) {
      Object.keys(dynamicCriteria.criteria_inputs).forEach((componentId) => {
        if (!domComponentIds.has(componentId)) {
          removeComponentInput(componentId);
        }
      });
    }
    const editorIsEmpty =
      (!editorRef.current.textContent?.trim() ||
        editorRef.current.textContent?.trim() === '') &&
      domComponentIds.size === 0;

    if (editorIsEmpty && dynamicCriteria?.criteria_inputs) {
      Object.keys(dynamicCriteria.criteria_inputs).forEach((id) => {
        removeComponentInput(id);
      });
    }
  }, [
    parseContent,
    updateCriteriaTemplate,
    removeComponentInput,
    dynamicCriteria,
  ]);

  // Component Portals Hook
  const { portals, renderComponentByType, setPendingPortalUpdate } =
    useComponentPortals({
      updateComponentInput,
      removeComponentInput,
      updateCriteria,
      editorRef,
      pendingPlaceholdersRef,
    });

  // Template Hydration Hook - handles initializing editor with template content
  useTemplateHydration({
    editorRef,
    lastContentRef,
    dynamicCriteria,
    renderComponentByType,
  });

  // Insert a component pill at the current cursor position
  const insertComponentPill = useCallback(
    (type: CriteriaComponentType, initialData?: Record<string, unknown>) => {
      if (!editorRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const savedSelection = selection.getRangeAt(0).cloneRange();
      const componentId = generateComponentId();

      // Create pill container
      const pillContainer = document.createElement('span');
      pillContainer.setAttribute('contentEditable', 'false');
      pillContainer.setAttribute('data-component-id', componentId);
      pillContainer.setAttribute('data-component-type', type);
      pillContainer.classList.add('component-pill');
      pillContainer.style.display = 'inline';
      pillContainer.style.verticalAlign = 'baseline';

      savedSelection.deleteContents();
      savedSelection.insertNode(pillContainer);

      const prevSibling = pillContainer.previousSibling;
      if (
        !prevSibling ||
        (prevSibling.nodeType === Node.TEXT_NODE &&
          !prevSibling.textContent?.endsWith(' '))
      ) {
        const spaceBefore = document.createTextNode(' ');
        pillContainer.parentNode?.insertBefore(spaceBefore, pillContainer);
      }
      const docSpace = document.createTextNode(' ');
      pillContainer.parentNode?.insertBefore(
        docSpace,
        pillContainer.nextSibling
      );
      const cursorAnchor = document.createTextNode('\u200B');
      docSpace.parentNode?.insertBefore(cursorAnchor, docSpace.nextSibling);

      const newRange = document.createRange();
      newRange.selectNodeContents(cursorAnchor);
      newRange.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(newRange);

      editorRef.current.focus();

      const componentData: ComponentData = {
        id: componentId,
        type: type as CriteriaComponentType,
        connector: 'ANY',
        text: '',
        codes: undefined,
        providers: undefined,
        specialties: undefined,
        payerTypes: undefined,
        payerNames: undefined,
        encounters: undefined,
        codeSets: undefined,
        crmStatus: undefined,
        filterSets: undefined,
      };

      // If we have initial data (from slash command), include it
      const inputData = initialData
        ? { ...initialData, ui_metadata: [initialData] }
        : {};

      renderComponentByType(pillContainer, componentData, inputData);
      const space = document.createTextNode(' ');
      const range = document.createRange();
      range.setStartAfter(pillContainer);
      range.insertNode(space);
      setTimeout(() => {
        if (editorRef.current) {
          moveCursorToEnd(editorRef.current);
        }
      }, 0);
      setTimeout(() => {
        updateCriteria();
      }, 0);

      return componentId;
    },
    [renderComponentByType, updateCriteria]
  );

  // Insert a component pill at a specific element (placeholder), hiding the placeholder
  // The placeholder is only permanently removed when data is confirmed
  const insertComponentPillAtElement = useCallback(
    (type: CriteriaComponentType, placeholderElement: HTMLElement) => {
      if (!editorRef.current) return;

      const componentId = generateComponentId();

      // Create pill container
      const pillContainer = document.createElement('span');
      pillContainer.setAttribute('contentEditable', 'false');
      pillContainer.setAttribute('data-component-id', componentId);
      pillContainer.setAttribute('data-component-type', type);
      pillContainer.classList.add('component-pill');
      pillContainer.style.display = 'inline';
      pillContainer.style.verticalAlign = 'baseline';

      // Hide the placeholder (don't remove yet) and insert pill after it
      placeholderElement.style.display = 'none';
      placeholderElement.parentNode?.insertBefore(
        pillContainer,
        placeholderElement.nextSibling
      );

      // Track this as a pending placeholder
      pendingPlaceholdersRef.current.set(componentId, {
        placeholder: placeholderElement,
        confirmed: false,
      });

      // Add space after if needed
      const nextSibling = pillContainer.nextSibling;
      if (
        !nextSibling ||
        (nextSibling.nodeType === Node.TEXT_NODE &&
          !nextSibling.textContent?.startsWith(' '))
      ) {
        const spaceAfter = document.createTextNode(' ');
        pillContainer.parentNode?.insertBefore(
          spaceAfter,
          pillContainer.nextSibling
        );
      }

      editorRef.current.focus();

      const componentData: ComponentData = {
        id: componentId,
        type: type as CriteriaComponentType,
        connector: 'ANY',
        text: '',
        codes: undefined,
        providers: undefined,
        specialties: undefined,
        payerTypes: undefined,
        payerNames: undefined,
        encounters: undefined,
        codeSets: undefined,
        crmStatus: undefined,
        filterSets: undefined,
      };

      // Pass flag indicating this component was created from a placeholder
      renderComponentByType(pillContainer, componentData, {
        _fromPlaceholder: true,
      });

      setTimeout(() => {
        updateCriteria();
      }, 0);

      return componentId;
    },
    [renderComponentByType, updateCriteria]
  );

  // Handle clicks on template placeholders
  const handleEditorClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;

      if (isPlaceholderElement(target)) {
        e.preventDefault();
        e.stopPropagation();

        const componentType = getPlaceholderType(target);
        if (componentType && effectiveAllowedTypes.includes(componentType)) {
          insertComponentPillAtElement(componentType, target);
        }
      }
    },
    [effectiveAllowedTypes, insertComponentPillAtElement]
  );

  // Slash Command Menu Hook
  const {
    showSlashMenu,
    slashMenuPosition,
    slashSearchTerm,
    detectSlashCommand,
    handleSlashSelect,
    handleSlashMenuClose,
  } = useSlashCommandMenu({
    editorRef,
    components,
    dynamicCriteria,
    portals,
    effectiveAllowedTypes,
    updateComponentInput,
    updateCriteria,
    insertComponentPill,
    setPendingPortalUpdate,
  });

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      lastContentRef.current = editorRef.current.innerHTML;
      updateCriteria();
      detectSlashCommand();
    }
  }, [updateCriteria, detectSlashCommand]);

  // Use MutationObserver instead of polling for better performance
  useEffect(() => {
    if (!editorRef.current) return;

    const observer = new MutationObserver((mutations) => {
      if (!editorRef.current) return;

      // Skip mutations that occur inside component pills (e.g., dropdown open/close)
      // These are React portal updates and shouldn't trigger criteria recalculation
      const hasExternalChange = mutations.some((mutation) => {
        const target = mutation.target as HTMLElement;
        return !target.closest?.('[data-component-id]');
      });

      if (!hasExternalChange) return;

      const currentContent = editorRef.current.innerHTML;
      if (currentContent !== lastContentRef.current) {
        lastContentRef.current = currentContent;
        updateCriteria();
      }
    });

    observer.observe(editorRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [updateCriteria]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Ctrl+Space for component menu
      if ((e.ctrlKey || e.metaKey) && e.code === 'Space') {
        e.preventDefault();

        const selection = window.getSelection();
        if (!selection || !editorRef.current || selection.rangeCount === 0)
          return;

        const range = selection.getRangeAt(0);
        let rect = range.getBoundingClientRect();

        if (rect.left === 0 && rect.top === 0) {
          const tempSpan = document.createElement('span');
          tempSpan.style.position = 'absolute';
          tempSpan.innerHTML = '&nbsp;';

          range.insertNode(tempSpan);
          rect = tempSpan.getBoundingClientRect();
          tempSpan.remove();
          const newRange = document.createRange();
          newRange.setStart(range.startContainer, range.startOffset);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }

        setMenuPosition({
          x: rect.left,
          y: rect.bottom + 5,
        });
        setShowComponentMenu(true);
      } else if (showComponentMenu && e.key === 'Enter') {
        e.preventDefault();
        return;
      }
    },
    [showComponentMenu]
  );

  const handleComponentSelect = useCallback(
    (type: CriteriaComponentType) => {
      setShowComponentMenu(false);

      if (effectiveAllowedTypes.includes(type)) {
        insertComponentPill(type);
      }
    },
    [effectiveAllowedTypes, insertComponentPill]
  );

  return (
    <div className="flex flex-col pt-3 gap-2">
      {/* Styles for template placeholders */}
      <style>{`
        .template-placeholder {
          display: inline-block;
          padding: 2px 8px;
          margin: 0 2px;
          border: 1px dashed #6366f1;
          border-radius: 9999px;
          background-color: #eef2ff;
          color: #4f46e5;
          font-size: 0.875rem;
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s;
        }
        .template-placeholder:hover {
          background-color: #e0e7ff;
        }
      `}</style>
      <div className="flex flex-col gap-0.5">
        <Typography variant="h6" className="font-medium text-sm leading-tight">
          Define AI Criteria
        </Typography>
        <div className="flex items-center space-x-1 mb-2 flex-wrap">
          <Typography
            variant="body2"
            className="text-tertiary text-xs leading-tight"
          >
            Type
          </Typography>
          <div className="px-2 py-0.5 text-xs border border-neutral-200 flex items-center justify-center rounded-lg whitespace-nowrap">
            /
          </div>
          <Typography
            variant="body2"
            className="text-tertiary text-xs leading-tight"
          >
            to search or
          </Typography>
          <div className="px-2 py-0.5 text-xs border border-neutral-200 flex items-center justify-center rounded-lg whitespace-nowrap">
            Ctrl+Space
          </div>
          <Typography
            variant="body2"
            className="text-tertiary text-xs leading-tight"
          >
            to insert attributes
          </Typography>
        </div>
      </div>

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          className="relative w-full min-h-72 p-4 border border-neutral-50
            bg-neutral-50 cursor-text focus:outline-none"
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={handleEditorClick}
          data-placeholder="E.g., I want to find the top referring patients for diabetic depth-inlay shoes diagnosis... (Type / to search or Ctrl+Space to add components)"
        />
      </div>

      {/* Component Menu (Ctrl+Space) */}
      <ComponentMenu
        isOpen={showComponentMenu}
        position={menuPosition}
        onSelect={handleComponentSelect}
        onClose={() => setShowComponentMenu(false)}
        allowedTypes={effectiveAllowedTypes}
      />

      {/* Slash Command Menu (/) - Notion-style inline search */}
      <SlashCommandMenu
        isOpen={showSlashMenu}
        position={slashMenuPosition}
        searchTerm={slashSearchTerm}
        onSelect={handleSlashSelect}
        onClose={handleSlashMenuClose}
        allowedTypes={effectiveAllowedTypes}
      />

      {/* Render all portals */}
      {Array.from(portals.values()).map((portal) =>
        createPortal(portal.component, portal.target, portal.id)
      )}
    </div>
  );
};

export default DynamicCriteriaGeneratorV2;
